import { programLabel } from '@/lib/programs'
import { RubricComparison, shownImplementedRubric } from './RubricComparison'
import { RubricLinkProvider, revealRubricCheck } from './rubricLinks'
import { createPortal } from 'react-dom'
import { useCallback, useEffect, useId, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { BriefView } from '@/components/BriefForm'
import { CoachPanel } from '@/components/CoachPanel'
import { BuilderSubmission } from './BuilderSubmission'
import { WorkReview } from './WorkReview'
import { ReviewRollouts } from './ReviewRollouts'
import { ReviewFeedback } from './ReviewFeedback'
import { ReviewDecision } from './ReviewDecision'
import { ReleaseReview } from './ReleaseReview'
import { fmtDateTime } from '@/lib/format'
import { Byline, PersonName, feedbackSource } from '@/components/PersonName'
import type { Role } from '@/lib/types'
import type { ReviewDetail } from './types'
import { PeerExecution } from './PeerExecution'
import { Resubmission } from './Resubmission'
import { MarkedText, RubricWithChanges } from '@/components/ReviewChanges'
import { usePeerReviewDraft, type ReviewDraft } from './usePeerReviewDraft'
import { api } from '@/api/client'
import { Picker } from '@/components/Picker'
import { fmtDate } from '@/lib/format'
import { roundStatus } from './labels'
import type { PreviousSubmission } from './types'

interface Props {
  data: ReviewDetail
  /** Static materials and an unsaved example decision. No review actions or recovery writes. */
  example?: { draft: ReviewDraft; execution: (active: boolean) => ReactNode; rollouts: (active: boolean) => ReactNode; coach: ReactNode }
  role: Role
  standalone: boolean
  inTask: boolean
  active: boolean
  decisionTarget?: HTMLElement | null
  reviewPicker?: ReactNode
  busy: boolean
  failure: string
  action: (path: string, body?: unknown) => Promise<boolean | undefined>
  onDirty: (dirty: boolean) => void
}

/** SME peer and quality reviews share the same materials and decision layout. */
export function ExpertReview({
  data,
  example,
  role,
  standalone,
  inTask,
  active,
  decisionTarget,
  reviewPicker,
  busy,
  failure,
  action,
  onDirty,
}: Props) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const id = useId()
  const { round, submission } = data
  const quality = round.kind === 'quality'
  const title = quality ? 'Quality check' : 'Peer review'
  const task = submission.task
  const base = `/tasks/${task.task_id}/reviews/${round.round_id}`
  const isPeer = !!example || round.peer_id === user?.id
  const open = round.status === 'open' && data.is_current
  const [section, setSection] = useState(quality ? 'rollouts' : 'brief')
  const [visited, setVisited] = useState(new Set(['brief', ...(quality ? ['rollouts'] : [])]))
  const preparing = round.status === 'preparing'
  const visibleSection = preparing && section === 'rollouts' ? 'brief' : section
  const { draft, update, clear } = usePeerReviewDraft(user?.id ?? '', round.round_id, example?.draft)
  const { feedback, verdict } = draft
  const ownReview = data.recommendations.filter((item) => item.actor.user_id === user?.id).at(-1)
  const ownComments = data.comments.filter((item) => item.actor.user_id === user?.id)
  const [submitted, setSubmitted] = useState(false)
  // Mark what changed since an earlier peer review round: the previous one unless the reviewer picks another.
  const previous = !quality && !task.scientific ? data.previous_submission : null
  const earlier = !quality && !task.scientific && !inTask ? data.comparable_rounds ?? [] : []
  const roundLabel = (roundId: string) => `Review ${earlier.length - earlier.findIndex((item) => item.round_id === roundId)}`
  const [compareTo, setCompareTo] = useState(previous?.round_id ?? '')
  const [compared, setCompared] = useState<Record<string, PreviousSubmission>>({})
  const [compareError, setCompareError] = useState('')
  useEffect(() => {
    setCompareError('')
    if (!compareTo || compareTo === previous?.round_id || compared[compareTo]) return
    let live = true
    api.get<PreviousSubmission>(`${base}/compare/${compareTo}`)
      .then((value) => { if (live) setCompared((current) => ({ ...current, [compareTo]: value })) })
      .catch((error) => { if (live) setCompareError((error as Error).message) })
    return () => { live = false }
  }, [base, compareTo, compared, previous?.round_id])
  const comparison = !compareTo ? null : compareTo === previous?.round_id ? previous : compared[compareTo] ?? null
  const since = compareTo === previous?.round_id ? 'since last review' : `since ${roundLabel(compareTo)}`
  useEffect(() => {
    onDirty(!!feedback || !!verdict)
    return () => onDirty(false)
  }, [feedback, verdict, onDirty])
  async function submit() {
    if (example || !verdict || (verdict === 'needs_changes' && !feedback.trim())) return
    if (await action(`${base}/recommendations`, { version: round.version, verdict, feedback })) {
      update('verdict', '')
      update('feedback', '')
      setSubmitted(true)
    }
  }
  const reviewFeedback = (
    <ReviewFeedback
      busy={busy}
      base={base}
      action={action}
      role={role}
      open={open}
      data={{
        ...data,
        recommendations: data.recommendations.filter((item) => item.actor.user_id !== user?.id),
        comments: data.comments.filter((item) => item.actor.user_id !== user?.id),
      }}
    />
  )
  const peerCoach = example ? (!quality ? example.coach : undefined) :
    !quality && (isPeer || (role === 'engineer' && round.peer_id)) ? (
      <CoachPanel
        taskId={task.task_id}
        role={role}
        compact
        conversationUrl={`/tasks/${task.task_id}/conversations/peer-${round.round_id}`}
      />
    ) : undefined
  const tabs = [
    ...(quality && !preparing ? [{ id: 'rollouts', label: 'Rollouts' }] : []),
    { id: 'brief', label: task.scientific ? 'Prompt' : 'Brief' },
    { id: 'rubric', label: 'Rubric' },
    ...(!task.scientific || submission.files.length ? [{ id: 'execution', label: 'Execution' }] : []),
    ...(peerCoach ? [{ id: 'coach', label: 'Coach' }] : []),
    ...(quality ? [{ id: 'feedback', label: 'Feedback' }] : []),
  ]
  // A rollout result opens its check in this review's Rubric tab.
  const openRubricCheck = useCallback((checkId: string) => {
    setSection('rubric')
    setVisited((current) => new Set([...current, 'rubric']))
    revealRubricCheck(checkId, () => document.getElementById(`${id}-panel-rubric`))
  }, [id])
  function select(value: string) {
    setSection(value)
    setVisited((current) => new Set([...current, value]))
  }
  const status = preparing
    ? 'Preparing review'
    : open
      ? isPeer
        ? 'Your review'
        : quality
          ? 'In quality check'
          : 'In peer review'
      : round.status === 'approved'
        ? quality
          ? 'Approved'
          : 'Approved for build'
        : round.status === 'returned'
          ? quality
            ? round.return_to_stage === 2 ? 'Returned to expert execution' : 'Returned to build'
            : 'Returned to author'
          : 'Review closed'
  // Keep one mounted form and move its DOM into the task-wide rail, outside hidden tab content.
  const decision =
    (data.can_recommend && open) || (standalone && !open && !preparing) ? (
      <ReviewDecision
        quality={quality}
        readOnly={!open || !data.can_recommend}
        busy={busy}
        submitted={submitted}
        ownReview={ownReview}
        draft={draft}
        update={update}
        submit={submit}
        submitDisabled={!!example}
      >
        {quality && ownComments.length > 0 && (
          <details className="review-comment">
            <summary>Your comments ({ownComments.length})</summary>
            {ownComments.map((item) => (
              <article className="review-feedback" key={item.entry_id}>
                <p className="prewrap">{item.text}</p>
                <span className="small muted">
                  {fmtDateTime(item.at)}
                  {item.reference?.step ? ` · Step ${item.reference.step}` : ''}
                  {item.reference?.criterion_id ? ` · ${item.reference.criterion_id}` : ''}
                </span>
              </article>
            ))}
          </details>
        )}
        {isPeer && open && (
          <ReleaseReview
            busy={busy || !!example}
            hasDraft={!!feedback || !!verdict}
            onRelease={async () => {
              if (!example && await action(`${base}/release`)) {
                clear()
                navigate('/sme/reviews?mine=0')
              }
            }}
          />
        )}
        {failure && (
          <p className="note err" role="alert">
            {failure}
          </p>
        )}
      </ReviewDecision>
    ) : null
  return (
    <div className="peer-review">
      <header className="peer-review-heading">
        {standalone ? (
          <>
            <div className="task-meta">
              <span className="chip">{programLabel(task)}</span>
              <span>{title}</span>
            </div>
            <h1>{task.title}</h1>
          </>
        ) : (
          <h2>{title}</h2>
        )}
        <div className="peer-review-meta muted small">
          {!inTask && <span><PersonName people={data.people} id={round.author_id} email={round.author_email} /></span>}
          <span>Submitted {fmtDateTime(round.created_at)}</span>
          <span>
            {inTask && open
              ? round.peer_email
                ? <>Peer: <PersonName people={data.people} id={round.peer_id} email={round.peer_email} /></>
                : 'Waiting for a peer reviewer'
              : status}
          </span>
        </div>
        {(reviewPicker || earlier.length > 0) && (
          <div className="peer-review-versions">
            {reviewPicker}
            {earlier.length > 0 && <>
              <span className="muted small">Compare to</span>
              <Picker label="Compare to" value={compareTo} onChange={setCompareTo} options={[
                { value: '', label: 'Nothing', detail: 'Show this review without marked changes' },
                ...earlier.map((item) => ({ value: item.round_id, label: roundLabel(item.round_id),
                  detail: `${fmtDate(item.created_at)} · ${roundStatus[item.status] ?? item.status}` })),
              ]} />
            </>}
            {compareError && <span className="small err" role="alert">{compareError}</span>}
          </div>
        )}
      </header>
      {!quality && data.agent_question && (
        <section className="note warn agent-blocker" aria-label="Agent issues">
          <h3>The authoring agent asked the task's expert a question</h3>
          <p>The build stopped with the question below. If you approve the task, the build resumes with the expert's answer.</p>
          <div className="who">Message from the authoring agent</div>
          <blockquote className="prewrap">{data.agent_question.message}</blockquote>
          <div className="who">Answer from <PersonName people={data.people} id={data.agent_question.returned_by_id}
            email={typeof data.agent_question.returned_by === 'string' ? data.agent_question.returned_by : null} fallback="the expert" /></div>
          <blockquote className="prewrap">{data.agent_question.response || 'No answer saved.'}</blockquote>
        </section>
      )}
      {!open && !preparing && !decision && (
        <div className="peer-review-outcome" aria-label="Saved review feedback">
          <div>
            <strong>{status}</strong>
            {data.recommendations.filter(item => !quality || item.actor.user_id === user?.id).map((item) => (
              <article className="review-feedback" key={item.entry_id}>
                <div className="small muted">
                  <Byline people={data.people} id={item.actor.user_id} email={item.actor.email}
                    source={feedbackSource(item.actor.role, role, item.actor.user_id === (round.author_id ?? task.sme_id))} />
                </div>
                <p className="prewrap">{item.feedback}</p>
              </article>
            ))}
          </div>
        </div>
      )}
      <Resubmission data={data} role={role} />
      <div
        className={`peer-review-layout ${decision && !decisionTarget ? 'with-decision' : ''}`}
      >
        <div className="peer-review-main">
          {inTask ? (
            <WorkReview
              role={role}
              data={data}
              active={active}
              feedback={reviewFeedback}
              coach={peerCoach}
            />
          ) : (
            <RubricLinkProvider spec={shownImplementedRubric(task, submission.implemented_rubric)} open={openRubricCheck}>
              <div className="peer-review-tabs" role="tablist" aria-label={`${title} materials`}>
                {tabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    id={`${id}-tab-${tab.id}`}
                    role="tab"
                    type="button"
                    aria-selected={visibleSection === tab.id}
                    aria-controls={`${id}-panel-${tab.id}`}
                    tabIndex={visibleSection === tab.id ? 0 : -1}
                    onClick={() => select(tab.id)}
                    onKeyDown={(event) => {
                      const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0
                      const next = offset
                        ? (index + offset + tabs.length) % tabs.length
                        : event.key === 'Home'
                          ? 0
                          : event.key === 'End'
                            ? tabs.length - 1
                            : -1
                      if (next >= 0) {
                        event.preventDefault()
                        select(tabs[next].id)
                        document.getElementById(`${id}-tab-${tabs[next].id}`)?.focus()
                      }
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  id={`${id}-panel-${tab.id}`}
                  role="tabpanel"
                  aria-labelledby={`${id}-tab-${tab.id}`}
                  hidden={visibleSection !== tab.id}
                  className="peer-review-material"
                >
                  {visited.has(tab.id) && (
                    <>
                      {tab.id === 'brief' && (
                        <>
                          <section className="peer-agent-prompt">
                            <h3>Agent prompt</h3>
                            {comparison
                              ? <p className="prewrap diff"><MarkedText before={comparison.prompt} after={task.prompt} /></p>
                              : <p className="prewrap">{task.prompt}</p>}
                          </section>
                          {!task.scientific && <BriefView brief={task.brief} before={comparison?.brief ?? undefined} />}
                        </>
                      )}
                      {tab.id === 'rubric' && (
                        <>
                          <RubricComparison task={task} role={role} implementedRubric={submission.implemented_rubric}
                            authorRubric={comparison ? <RubricWithChanges task={task} inline
                              before={{ prompt: comparison.prompt, rubric: comparison.rubric, rubric_layout: comparison.rubric_layout }} /> : undefined} />
                          {quality && <BuilderSubmission task={task} role={role} />}
                        </>
                      )}
                      {tab.id === 'execution' && (
                        example?.execution(section === 'execution') ?? <PeerExecution
                          base={base}
                          submission={submission}
                          active={section === 'execution'}
                          changes={comparison?.files}
                          since={since}
                        />
                      )}
                      {tab.id === 'rollouts' && (
                        example?.rollouts(section === 'rollouts') ?? <ReviewRollouts
                          taskId={task.task_id}
                          roundId={round.round_id}
                          active={section === 'rollouts'}
                        />
                      )}
                      {tab.id === 'feedback' && reviewFeedback}
                      {tab.id === 'coach' && peerCoach}
                    </>
                  )}
                </div>
              ))}
            </RubricLinkProvider>
          )}
        </div>
        {decisionTarget ? decision && createPortal(decision, decisionTarget) : decision}
      </div>
      {failure && !decision && (
        <p className="note err" role="alert">
          {failure}
        </p>
      )}
    </div>
  )
}
